/** Classificação de gesto de arrasto — pura, porque o jsdom não tem
 * `PointerEvent` construível nem geometria.
 *
 * Um teste que simulasse "swipe" com eventos sintéticos e coordenadas
 * inventadas mediria o dublê, não o gesto. Aqui a decisão é uma função de dois
 * pontos e do estado do leitor; o hook só coleta os pontos.
 */

export type Swipe = 'anterior' | 'proxima' | 'nenhum'

/** Deslocamento mínimo para virar página. Abaixo disso é toque trêmulo. */
export const DISTANCIA_MIN = 60

/** Quanto o eixo horizontal precisa dominar o vertical.
 *
 * Sem isso, rolar a página verticalmente com o polegar em diagonal — que é
 * como se rola um celular de uma mão só — vira página a cada rolagem.
 */
export const DOMINANCIA = 1.5

/** Acima disto o gesto é rápido demais para ser rolagem: é um flick. */
export const VELOCIDADE_FLICK = 0.5 // px/ms

export interface Ponto {
  x: number
  y: number
  t: number
}

export interface Contexto {
  /** `true` quando a página está mais larga que a área visível.
   *
   * Aí o arrasto horizontal é ROLAGEM, não virada: quem ampliou uma tabela
   * precisa chegar à coluna da direita, e virar a página no lugar disso é a
   * forma mais rápida de tornar o zoom inútil. */
  rolagemHorizontal: boolean
  /** Mouse não faz swipe: arrastar com o botão pressionado é SELECIONAR. */
  ehMouse: boolean
}

export function classificarSwipe(
  inicio: Ponto,
  fim: Ponto,
  contexto: Contexto,
): Swipe {
  if (contexto.ehMouse || contexto.rolagemHorizontal) return 'nenhum'

  const dx = fim.x - inicio.x
  const dy = fim.y - inicio.y
  const horizontal = Math.abs(dx)
  const vertical = Math.abs(dy)

  if (horizontal < DISTANCIA_MIN) return 'nenhum'
  if (horizontal < vertical * DOMINANCIA) return 'nenhum'

  // Arrasto longo e LENTO é leitura com o dedo apoiado, não virada de página.
  const duracao = Math.max(1, fim.t - inicio.t)
  if (horizontal / duracao < VELOCIDADE_FLICK && horizontal < DISTANCIA_MIN * 2) {
    return 'nenhum'
  }

  // Arrastar para a ESQUERDA avança — o conteúdo acompanha o dedo, como numa
  // pilha de folhas. O inverso é o erro clássico e parece o leitor travado.
  return dx < 0 ? 'proxima' : 'anterior'
}

/** A página transborda a área visível na horizontal? (ver `rolagemHorizontal`) */
export function temRolagemHorizontal(
  larguraDaPagina: number,
  larguraVisivel: number,
  tolerancia = 4,
): boolean {
  if (larguraVisivel <= 0) return false
  return larguraDaPagina > larguraVisivel + tolerancia
}

/** Aceita o que o aluno digitou no campo "Página", ou `null` se não der.
 *
 * `null` e não "volta para 1": apagar o campo para digitar outro número é o
 * caminho normal, e saltar para a página 1 no meio da digitação faria o leitor
 * pular de lugar a cada tecla.
 */
export function paginaDigitada(texto: string, total: number): number | null {
  const limpo = texto.trim()
  if (limpo === '' || !/^\d+$/.test(limpo)) return null
  const numero = Number(limpo)
  if (numero < 1) return null
  if (total > 0 && numero > total) return total
  return numero
}
